import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { useTenantRole } from "../TenantRoleDisplay";
import { AuthContext } from "../../context/authcontext";
import { all_routes } from "../../feature-module/router/all_routes";
import { CreditCard } from "lucide-react";

interface SettingsTabsNavigationProps {
  activeTab: string;
  variant?: "bottom" | "standard" | "solid";
  includeIcons?: boolean;

  context?: "profile" | "organization" | "access-subscription" | "tenant";
}

const SettingsTabsNavigation: React.FC<SettingsTabsNavigationProps> = ({
  activeTab,
  variant = "standard",
  includeIcons = false,
  context,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const route = all_routes;

  const isTabAllowedByContext = (tabName: string) => {
    if (!context) return true;
    switch (context) {
      case "profile":
        return [
          "general",
          "integration",
          "api-integration",
          "notification-settings",
        ].includes(tabName);
      case "organization":
        return [
          "organization",
          "lifecycle",
          "workflow-configuration",
          "source-settings",
          "lifecycle-status-reason",
          "import",
          "products-services",
          "custom-fields",
          "audit-log",
          "approval-policy",
          "auto-creation-rules",
        ].includes(tabName);
      case "access-subscription":
        return ["access", "subscription"].includes(tabName);
      case "tenant":
        return ["tenant"].includes(tabName);
      default:
        return true;
    }
  };

  const shouldShowTab = (tabName: string) => {
    if (!userRole) return false;

    const roleKey = userRole.role_key;
    const isSuperAdmin =
      roleKey === "SUPERADMIN" || roleKey === "PLATFORM_ANALYST" || roleKey === "APIADMIN";
    const hasNoTenantSelected = !selectedTenantId;

    if (
      isSuperAdmin &&
      hasNoTenantSelected &&
      ["organization", "access", "subscription"].includes(tabName)
    ) {
      return false;
    }

    switch (tabName) {
      case "general":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
          "APIADMIN",
        ].includes(roleKey);
      case "organization":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
          "APIADMIN",
        ].includes(roleKey);
      case "lifecycle":
        return [
          "CLIENTADMIN",
          "SALESUSER",
          "SUPERADMIN",
          "PLATFORM_ANALYST",
          "APIADMIN",
        ].includes(roleKey);
      case "workflow-configuration":
        return [
          "CLIENTADMIN",
          "SALESUSER",
          "SUPERADMIN",
          "PLATFORM_ANALYST",
          "APIADMIN",
        ].includes(roleKey);
      case "source-settings":
        return [
          "CLIENTADMIN",
          "SALESUSER",
        ].includes(roleKey);
      case "lifecycle-status-reason":
        return [
          "CLIENTADMIN",
          "SALESUSER",
        ].includes(roleKey);
      case "access":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST", "APIADMIN"].includes(
          roleKey
        );
      case "subscription":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST", "APIADMIN"].includes(
          roleKey
        );
      case "tenant":
        return roleKey === "SUPERADMIN" || roleKey === "PLATFORM_ANALYST" || roleKey === "APIADMIN";
      case "import":
        return ["CLIENTADMIN", "SALESUSER"].includes(roleKey);
      case "integration":
        return ["CLIENTADMIN", "SALESUSER"].includes(roleKey);
      case "api-integration":
        return ["CLIENTADMIN"].includes(roleKey);
      case "notification-settings":
        return ["CLIENTADMIN", "SALESUSER"].includes(roleKey);
      case "products-services":
        return ["CLIENTADMIN", "SALESUSER"].includes(roleKey);
      case "custom-fields":
        return ["CLIENTADMIN"].includes(roleKey);
      case "audit-log":
        return ["CLIENTADMIN"].includes(roleKey);
      case "approval-policy":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(roleKey);
      case "auto-creation-rules":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(roleKey);
      default:
        return false;
    }
  };

  const getNavClass = () => {
    switch (variant) {
      case "bottom":
        return "nav nav-tabs nav-tabs-bottom";
      case "solid":
        return "nav nav-tabs nav-tabs-solid nav-tabs-rounded nav-justified";
      default:
        return "nav nav-tabs";
    }
  };

  const getNavItemClass = () => {
    return variant === "bottom" ? "nav-item me-3" : "nav-item";
  };

  const getNavLinkClass = (tabName: string) => {
    const baseClass = variant === "bottom" ? "nav-link px-0" : "nav-link";
    return `${baseClass} ${activeTab === tabName ? "active" : ""}`;
  };

  return (
    <ul className={getNavClass()}>
      {isTabAllowedByContext("general") && shouldShowTab("general") && (
        <li className={getNavItemClass()}>
          <Link className={getNavLinkClass("general")} to={route.profile}>
            {includeIcons && <i className="ti ti-settings-cog" />} General
            Settings
          </Link>
        </li>
      )}
      {isTabAllowedByContext("organization") &&
        shouldShowTab("organization") && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("organization")}
              to={route.companySettings}
            >
              {includeIcons && <i className="ti ti-world-cog" />}{" "}
              {context === "organization"
                ? "General Settings"
                : "Organization Settings"}
            </Link>
          </li>
        )}
      {isTabAllowedByContext("lifecycle") && shouldShowTab("lifecycle") && (
        <li className={getNavItemClass()}>
          <Link
            className={getNavLinkClass("lifecycle")}
            to={route.leadssettings}
          >
            {includeIcons && <i className="ti ti-activity" />} LifeCycle
            Settings
          </Link>
        </li>
      )}
      {isTabAllowedByContext("workflow-configuration") && shouldShowTab("workflow-configuration") && (
        <li className={getNavItemClass()}>
          <Link
            className={getNavLinkClass("workflow-configuration")}
            to={route.workflowConfiguration}
          >
            {includeIcons && <i className="fa-solid fa-diagram-project me-1" />} Workflow
            Configuration
          </Link>
        </li>
      )}

      {((isTabAllowedByContext("custom-fields") && shouldShowTab("custom-fields")) ||
        (isTabAllowedByContext("lifecycle-status-reason") && shouldShowTab("lifecycle-status-reason")) ||
        (isTabAllowedByContext("source-settings") && shouldShowTab("source-settings"))) && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("field-configuration")}
              to={route.customFields}
            >
              {includeIcons && <i className="ti ti-forms"></i>} Field
              Configuration
            </Link>
          </li>
        )}
      {isTabAllowedByContext("access") && shouldShowTab("access") && (
        <li className={getNavItemClass()}>
          <Link
            className={getNavLinkClass("access")}
            to={route.accessManagement}
          >
            {includeIcons && <i className="ti ti-lock" />} Access Management
          </Link>
        </li>
      )}
      {isTabAllowedByContext("subscription") &&
        shouldShowTab("subscription") && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("subscription")}
              to={route.subscriptionmanagement}
            >
              {includeIcons && <CreditCard size={20} />} Subscription Management
            </Link>
          </li>
        )}
      {isTabAllowedByContext("tenant") && shouldShowTab("tenant") && (
        <li className={getNavItemClass()}>
          <Link
            className={getNavLinkClass("tenant")}
            to={route.tenantManagement}
          >
            {includeIcons && <i className="ti ti-building-community" />}{" "}
            Organization Management
          </Link>
        </li>
      )}
      {isTabAllowedByContext("import") && shouldShowTab("import") && (
        <li className={getNavItemClass()}>
          <Link className={getNavLinkClass("import")} to={route.importSettings}>
            {includeIcons && <i className="ti ti-file-import" />} Import
          </Link>
        </li>
      )}
      {isTabAllowedByContext("integration") && shouldShowTab("integration") && (
        <li className={getNavItemClass()}>
          <Link
            className={getNavLinkClass("integration")}
            to={route.emailSettings}
          >
            {includeIcons && <i className="fa fa-plug" />} Integration Settings
          </Link>
        </li>
      )}

      {/* Notification Settings Tab */}
      {isTabAllowedByContext("notification-settings") &&
        shouldShowTab("notification-settings") && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("notification-settings")}
              to={route.notificationSettings}
            >
              {includeIcons && <i className="ti ti-bell-ringing" />}{" "}
              Notification Settings
            </Link>
          </li>
        )}

      {/* API Integration Tab */}

      {isTabAllowedByContext("api-integration") &&
        shouldShowTab("api-integration") && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("api-integration")}
              to={route.apiIntegrationSettings}
            >
              {includeIcons && <i className="ti ti-api" />} API Integration
            </Link>
          </li>
        )}

      {/* API Integration Tab */}

      {isTabAllowedByContext("products-services") &&
        shouldShowTab("products-services") && (
          <li className={getNavItemClass()}>
            <Link
              className={getNavLinkClass("products-services")}
              to={route.productServices}
            >
              {includeIcons && <i className="fa-solid fa-cart-shopping" />}{" "}
              Products & Services
            </Link>
          </li>
        )}

      {isTabAllowedByContext("audit-log") && shouldShowTab("audit-log") && (
        <li className={getNavItemClass()}>
          <Link className={getNavLinkClass("audit-log")} to={route.auditLog}>
            {includeIcons && <i className="ti ti-file-analytics" />} Audit Log
          </Link>
        </li>
      )}
    </ul>
  );
};

export default SettingsTabsNavigation;
